import { combineReducers, createSelector, compose } from '@reduxjs/toolkit';
import { round } from 'lodash';

import slice from './multiphotos.slice';
import multiphotosAdapter from './multiphotos.adapter';

import cropsModule from 'domains/crops';
import photoModule from 'domains/photos';
import products from 'domains/products';

import activeEditor from 'domains/active_editor';
import { selectDefaultPPI } from 'selectors/ppi';

import { calculateBoundDimension } from 'utils/bounds_util';
import { validDimension } from 'utils/type_guards';
import { orientationAndRatioPercentage } from 'utils/math_util';
import { fitImageToNode } from 'utils/pan_util';

import type { CartProductPhotoCreateParams } from 'domains/cart_products';

/** Represents the slice as it exists in the final store's reducer */
const combined = combineReducers({ [slice.name]: slice.reducer });
type GlobalState = ReturnType<typeof combined>;

/** Get the local state for the current domain */
const localState = (state: GlobalState) => state[slice.name];
type LocalState = ReturnType<typeof localState>;

const defaultSelectors = multiphotosAdapter.getSelectors();

const selectSelectedPhotoIds = createSelector(
  [compose(defaultSelectors.selectIds, localState), cropsModule.selectors.selectAll],
  (multiphotoIds, crops) => {
    if (multiphotoIds.length > 0) {
      return multiphotoIds;
    }

    return crops.map((crop) => crop.photoId);
  }
);

type CartProductMultiPhotoCreateParams = CartProductPhotoCreateParams & { autoRotate?: boolean };
const selectMultiPhotosForCreateCartProduct = createSelector(
  [
    compose(defaultSelectors.selectAll, localState),
    photoModule.selectors.selectEntities,
    products.selectors.selectEntities,
    activeEditor.selectors.canvas,
    selectDefaultPPI,
  ],
  (
    multiphotos,
    photosById,
    productsById,
    activeCanvas,
    ppi
  ): CartProductMultiPhotoCreateParams[] => {
    const withImageDimensions = multiphotos.filter((mp) => mp.photoLoaded);

    if (!activeCanvas || !activeCanvas.product || multiphotos.length !== withImageDimensions.length)
      throw new Error('Not Enough Data');

    const product = productsById[activeCanvas.product];
    const node = product?.nodes.find((n) => n.node_type === 1);
    if (!product || !node) throw new Error('Product or Node Not Found');

    const [, , w, h] = node.bounds
      .split(',')
      .map((b) => calculateBoundDimension(b, 'image', product, activeCanvas) * ppi);

    const { orientation: nodeOrientation } = orientationAndRatioPercentage(w, h);

    return multiphotos.map((mp) => {
      const photo = photosById[mp.id];
      if (!photo) throw new Error('Photo Not Found');

      const imageDimensions = { height: mp.imageHeight, width: mp.imageWidth };
      if (!validDimension(imageDimensions)) throw new Error('Invalid Image Dimension');

      const { orientation: imageOrientation } = orientationAndRatioPercentage(
        imageDimensions.width,
        imageDimensions.height
      );

      const autoRotate =
        imageOrientation !== 'square' &&
        imageOrientation !== nodeOrientation &&
        product.rotation_allowed &&
        nodeOrientation !== 'square' &&
        !products.utils.isCustomSizeProduct(product);

      const nodeDimensions = autoRotate ? { width: h, height: w } : { width: w, height: h };

      const { fitCropH, fitCropW } = fitImageToNode(nodeDimensions, imageDimensions);

      return {
        autoRotate,
        filename: photo.original_filename,
        url: photo.ink_url,
        external_asset_id: photo.id,
        rotation: 0,
        node_id: node.id,
        crop: {
          x: 50,
          y: 50,
          w: round(fitCropW * 100, 6),
          h: round(fitCropH * 100, 6),
        },
      };
    });
  }
);

export default {
  ...multiphotosAdapter.getSelectors(localState),
  selectedPhotoIds: selectSelectedPhotoIds,
  forCreateCartProduct: selectMultiPhotosForCreateCartProduct,
};
